<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
// Автоочищаемый слушатель событий (VueUse)
import { useEventListener } from '@vueuse/core'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { updateLogoVisibility } from '@/utils/updateLogoVisibility'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useSettingsStore } from '@/modules/settings/store/settingsStore'
import { useAuthStore } from '@/modules/auth/stores/authStore'
// Перечисление имён маршрутов — для определения видимости лого
import { ERouteNames } from '@/router/ERouteNames'
// SVG логотипа (импорт для v-img)
import mainLogoSvg from '@/assets/icons/mainLogo.svg'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
import { consumeAppCheckoutQuery } from '@/utils/appCheckoutFromPlay'

// Глобальные компоненты обратной связи
import LoaderOverlay from '@/modules/feedback/components/LoaderOverlay.vue'
// Нижняя навигация
import BottomNav from '@/components/BottomNav.vue'

// Инициализация сторов и роутера
const feedbackStore = useFeedbackStore()
const settingsStore = useSettingsStore()
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

// Ref на элемент логотипа (для управления opacity при скролле)
const logoRef = ref<HTMLElement | null>(null)

/**
 * Обновляет CSS-переменную высоты вьюпорта
 */
const updateViewportHeight = () => {
  const vh = window.innerHeight * 0.01
  document.documentElement.style.setProperty('--tg-vh', `${vh}px`)
}

// ── Логотип ──────────────────────────────────────────

/** Лого видно на HOME и AUTH, если включено в настройках */
const isLogoVisible = computed(() => {
  return feedbackStore.showMainLogo
    && (route.name === ERouteNames.HOME || route.name === ERouteNames.AUTH)
})

/**
 * Добавляет/убирает класс has-logo-active на body
 * Класс управляет дополнительным padding-top для контента
 *
 * @param isVisible — показывать ли лого
 */
const toggleLogoClass = (isVisible: boolean): void => {
  if (isVisible) {
    document.body.classList.add('has-logo-active')
  } else {
    document.body.classList.remove('has-logo-active')
  }
}

// Watcher: синхронизируем класс body с computed видимости лого
watch(isLogoVisible, (newValue) => {
  toggleLogoClass(newValue)
}, { immediate: true })

// Watcher: при загрузке настроек синхронизируем showMainLogo из бекенда
watch(
  () => settingsStore.showMainLogo,
  (newValue) => {
    feedbackStore.showMainLogo = newValue
  },
  { immediate: true },
)

/** Показывать ли отступ под нижний навбар */
const isBottomNavVisible = computed(() => {
  if (!authStore.isAuth) return false
  const hiddenRoutes = [
    ERouteNames.AUTH,
    ERouteNames.PAYMENT,
    ERouteNames.MAGIC_LINK,
    ERouteNames.NOT_FOUND,
  ]
  const currentName = route.name as ERouteNames
  return !hiddenRoutes.includes(currentName)
})

// ── Обработчик скролла для fade лого ─────────────────

/**
 * Обёртка над updateLogoVisibility — передаёт ref элемента лого
 */
const handleScroll = () => {
  if (logoRef.value) {
    updateLogoVisibility(logoRef.value)
  }
}

// ── Lifecycle ────────────────────────────────────────

onMounted(async () => {
  // Вход из приложения (query) — до early-return без Telegram WebApp
  const cleanedQuery = await consumeAppCheckoutQuery(route.query)
  if (cleanedQuery) {
    await router.replace({ path: route.path, query: cleanedQuery })
  }

  const tg = window.Telegram?.WebApp
  if (!tg) return

  tg.ready()
  tg.expand()
  tg.enableClosingConfirmation()
  // Отключаем свайп по контенту для сворачивания — сворачивание остаётся через TG-бар (Bot API 7.7+)
  tg.disableVerticalSwipes()

  // Класс для CSS-правил специфичных для Telegram Mini App (только реальный TG-контекст)
  if (isTelegram()) {
    document.body.classList.add('is-telegram')
  }

  // Инициализация отслеживания скролла для fade лого
  window.addEventListener('scroll', handleScroll)

  // Вызовем сразу для начального состояния
  handleScroll()

  // Обработчик для iOS — скрыть клавиатуру при тапе вне input/textarea
  document.addEventListener('touchend', (event: TouchEvent) => {
    const target = event.target as HTMLElement | null
    if (target && !target.closest('input, textarea')) {
      document
        .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
        .forEach((input) => input.blur())
    }
  })
  window.addEventListener('resize', updateViewportHeight)

  // Проброс скролла колёсиком когда курсор вне .v-application__wrap (веб-версия)
  await nextTick()
  const wrap = document.querySelector('.v-application__wrap') as HTMLElement | null
  if (wrap) {
    useEventListener(document, 'wheel', (e: WheelEvent) => {
      if (!wrap.contains(e.target as Node)) {
        wrap.scrollTop += e.deltaY
      }
    }, { passive: true })
  }
})

// Очистка слушателей при размонтировании
onUnmounted(() => {
  window.removeEventListener('resize', updateViewportHeight)
  window.removeEventListener('touchend', updateViewportHeight)
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <VApp :class="{ 'has-bottom-nav': isBottomNavVisible }">
    <!-- Глобальный toast (v-snackbar) -->
    <v-snackbar
      v-model="feedbackStore.toastVisible"
      :timeout="feedbackStore.toastDuration"
      :color="feedbackStore.toastType"
      location="top"
      style="z-index: 100005 !important;"
    >
      {{ feedbackStore.toastMessage }}
      <template #actions>
        <v-btn
          variant="text"
          @click="feedbackStore.hideToast()"
        >
          <v-icon>ri-close-line</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <loader-overlay />
    <!-- Декоративный логотип -->
    <div
      v-if="isLogoVisible"
      ref="logoRef"
      class="app-logo"
      :class="{ 'app-logo--compact': isTelegram() }"
    >
      <v-img
        :src="mainLogoSvg"
        :width="isTelegram() ? 120 : 190"
        alt=""
      />
    </div>

    <RouterView />
    <BottomNav />
  </VApp>
</template>

<style scoped>
:deep(.v-application__wrap) {
  min-height: calc(100dvh - var(--body-padding-top, 0px) - var(--safe-area-bottom, 0px)) !important;
  width: 100%;

  /* Скрываем полосу прокрутки визуально */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.has-bottom-nav :deep(.v-application__wrap) {
  min-height: calc(100dvh - var(--body-padding-top, 0px) - var(--body-padding-bottom, 0px)) !important;
}

/* Декоративный логотип — в потоке и центрирован по горизонтали */
.app-logo {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50vw;
  max-width: 190px;
  margin: 0 auto;
  z-index: 3;
  pointer-events: none;
  transition: opacity 0.2s;
}

/* Уменьшенный логотип в Telegram Mini App */
.app-logo--compact {
  max-width: 120px;
}

/* Сдвиг всего контента приложения вниз при загруженной рекламе */
:global(body.ad-active .v-application) {
  padding-top: calc(54px + max(8px, env(safe-area-inset-top, 0px), var(--tg-content-safe-area-inset-top, 0px))) !important;
}

/* Глобальный z-index для снэкбара выше оверлея лоадера */
:global(.v-overlay-container .v-snackbar) {
  z-index: 100005 !important;
}
</style>
